<template xmlns="http://www.w3.org/1999/html">
    <div class="vancouver">
        <div class="introduction">
            <div class="introduction__left">
                <h2>밴쿠버 스타일 인용 가이드</h2>
                <h3>개요</h3>
                <p>
                  저자 번호 체계(author-number system)로도 알려진 밴쿠버 스타일은 <a href="https://www.ieee.org/" target="_blank">IEEE</a>와 <a href="https://www.nlm.nih.gov/bsd/medline.html" target="_blank">MEDLINE</a>을 포함한 수백 개의 과학 저널에 의해 채택되었습니다. 과학 저널은 참조에 고유 저자 지침(author guidelines)을 가지고 있지만, 이 지침들은 대부분 저자 번호 체계(author-number system)의 논리를 따르고 있습니다. 밴쿠버 스타일은 구체적인 형식과 상관 없이 모든 저자 번호 체계를 포괄한다는 점에서 다른 스타일들과 차별화됩니다.
                </p>
            </div>

            <div class="introduction__right">
                <img src="https://pstatic.wordvice.com/citation-generator/quick-guide/van-pub-manual.jpg" alt="MLA Handbook">
            </div>

            <p>
              이 인용 가이드는 밴쿠버 2nd Edition의 중요한 정보와 지침을 소개합니다. 에세이리뷰는 또한 <a href="/citation-guide/mla">MLA 7과 8</a>, <a href="/citation-guide/apa" >APA 6과 7</a>, and <a href="/citation-guide/chicago">시카고 17</a> 스타일을 위한 무료 스타일 가이드를 제공하고 있습니다.
            </p>
        </div>

        <div class="tables-of-contents-container" id="tables-of-contents-container">
            <div class="tables-of-contents on" id="tables-of-contents">
                <b>
                  목차
                </b>
                <ul class="tables-of-contents__list">
                    <li>
                        <a href="#intext">밴쿠버 스타일 내주(In-Text Citations)</a>
                    </li>
                    <li>
                        <a href="#references">밴쿠버 스타일 참고문헌 목록 (Reference List)</a>
                    </li>
                    <li>
                        <a href="#formatting">밴쿠버 스타일 참고문헌 (Reference) 페이지 작성 방법</a>
                    </li>
                    <li>
                        <a href="#bibliography">밴쿠버 스타일 서지목록 (Bibliography)</a>
                    </li>
                </ul>
            </div>
        </div>

        <!-- block -->
        <div class="block" id="intext">
            <h3>밴쿠버 스타일 내주 (In-Text Citations)</h3>
            <p>
              밴쿠버 스타일의 내주에는 위첨자 또는 괄호안의 숫자가 참조하는 저자나 연구 명 옆에 사용됩니다. 인용된 각 저작물에는 인용 순서에 따라 고유 번호가 할당되어야 합니다. 같은 저작물을 두 번 이상 인용할 경우 매번 같은 인용 번호를 사용해야 합니다.
            </p>
            <p>
                <b>내주 예시</b>
            </p>
            <p class="cite-style1">
                Diamond (1) states that the historical discrepancies between peoples are due to the ‘differences among peoples’ environments, not because of the biological differences among the peoples themselves’ (p.25).
            </p>
        </div> <!-- // block -->

        <!-- block -->
        <div class="block" id="references">
            <h3>밴쿠버 스타일 참고문헌 목록 (References)</h3>
            <p>밴쿠버 스타일 참고문헌은 번호를 매겨 인용 순서에 따라 나열합니다.</p>
            <p class="indent2">
                (1) Diamond J. <i>Guns, Germs, and Steel: The Fates of Human Societies</i>. New York City: W.W. Norton; 1997.
            </p>
            <p class="indent2">
                (2) Rand A. <i>The Art of Fiction: A Guide for Writers and Readers</i>. New York City: Plume; 2000.
            </p>
            <p class="indent2">
                (3) Ishiguro K. <i>Never Let Me Go</i>. London: Faber & Faber; 2005.
            </p>
        </div> <!-- // block -->

        <!-- block -->
        <div class="block" id="formatting">
            <div class="reference-list size2">
                <div class="reference-list__left">
                    <h3>밴쿠버 스타일 참고문헌 (Reference) 페이지 작성 방법</h3>
                    <p>
                      밴쿠버 스타일 필수 서지 정보:
                    </p>
                    <ol>
                        <li>저자(들) 이름</li>
                        <li>인용된 원본의 표제</li>
                        <li>발행 정보</li>
                    </ol>
                    <p>
                      모든 출처는 번호를 매기고 인용된 순서대로 나열해야 합니다.
                    </p>
                </div>

                <!-- right -->
                <div class="reference-list__right">
                    <svg class="bi bi-search view-ico" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                    </svg>
                    <a href="https://pstatic.wordvice.com/citation-generator/quick-guide/vancouver_reference_list_kr.png" target="_blank">
                        <img class="lightbox" src="https://pstatic.wordvice.com/citation-generator/quick-guide/vancouver_reference_list_kr.png" alt="Reference List">
                    </a>
                </div>
            </div>

            <br>
            <h3>밴쿠버 스타일 웹사이트</h3>
            <p class="indent2">
                <span class="highlight--blue">[저자의 성]</span> <span class="highlight--pink">[저자 이름 이니셜]</span>. <span class="highlight--yellow">[<i>웹페이지 제목</i>]</span>. <span class="highlight--light-purple">Available from: [URL]</span> <span class="highlight--light-green">[Accessed [검색날짜]]</span>.
            </p>
            <p class="indent2">
                <span class="highlight--blue">Kim</span> <span class="highlight--pink">A</span>. <span class="highlight--yellow"><i>The History and Big Business of Academic Publishing</i></span>. <span class="highlight--light-purple">Available from: https://medium.com/wordviceediting/the-history-and-big-business-of-academic-publishing-90a23bb7cc4c</span> <span class="highlight--light-green">[Accessed 25th January 2021]</span>.
            </p>

            <br>
            <h3>밴쿠버 스타일 연속간행물 (학술지,잡지) (Print)</h3>
            <p class="indent2">
                <span class="highlight--blue">[저자의 성]</span> <span class="highlight--pink">[저자 이름 이니셜]</span>. <span class="highlight--yellow">[논문명]</span>. <span class="highlight--light-purple">[<i>저널명</i>]</span>. <span class="highlight--light-green">[발행 연도]</span>; <span class="highlight--blue">[권]</span>(<span class="highlight--pink">[호]</span>): <span class="highlight--yellow">[논문 수록 페이지 구간]]</span>.
            </p>
            <p class="indent2">
                <span class="highlight--blue">Encarnación-Pinedo</span> <span class="highlight--pink">E</span>. <span class="highlight--yellow">On Webbed Monsters, Revolutionary Activists and Plutonium Glow: Eco-Crisis in Diane di Prima and Anne Waldman</span>. <i class="highlight--light-purple">Humanities</i>. <span class="highlight--light-green">2020</span>; <span class="highlight--blue">10</span>(<span class="highlight--pink">4</span>): <span class="highlight--yellow">1-14</span>.
            </p>

            <br>
            <h3>밴쿠버 스타일 연속간행물 (학술지,잡지) (Electronic)</h3>
            <p class="indent2">
                <span class="highlight--blue">[저자의 성]</span> <span class="highlight--pink">[저자 이름 이니셜]</span>. <span class="highlight--yellow">[논문명]</span>. <span class="highlight--light-purple">[<i>저널명</i>>]</span>. <span class="highlight--light-green">[발행 연도]</span>; <span class="highlight--blue">[권]</span>(<span class="highlight--pink">[호]</span>): <span class="highlight--yellow">[논문 수록 페이지 구간]</span>. <span class="highlight--light-purple">Available from: [URL or DOI]</span> <span class="highlight--light-green">[Accessed [Accessed Date]]</span>.
            </p>
            <p class="indent2">
                <span class="highlight--blue">Encarnación-Pinedo</span> <span class="highlight--pink">E</span>. <span class="highlight--yellow">On Webbed Monsters, Revolutionary Activists and Plutonium Glow: Eco-Crisis in Diane di Prima and Anne Waldman</span>. <i class="highlight--light-purple">Humanities</i>. <span class="highlight--light-green">2020</span>; <span class="highlight--blue">10</span>(<span class="highlight--pink">4</span>): <span class="highlight--yellow">1-14</span>. <span class="highlight--light-purple">Available from: https://medium.com/wordviceediting/the-history-and-big-business-of-academic-publishing-90a23bb7cc4c</span> <span class="highlight--light-green">[Accessed 25th January 2021]</span>.
            </p>

            <br>
            <h3>밴쿠버 단행본(도서)</h3>
            <p class="indent2">
                <span class="highlight--blue">[저자의 성]</span> <span class="highlight--pink">[저자 이름 이니셜]</span>. <span class="highlight--yellow">[<i>표제</i>]</span>. <span class="highlight--light-purple">[발행지]</span>: <span class="highlight--light-green">[발행처]</span>; <span class="highlight--blue">[발행 연도]</span>.
            </p>
            <p class="indent2">
                <span class="highlight--blue">Diamond</span> <span class="highlight--pink">J</span>. <i class="highlight--yellow">Guns, Germs, and Steel: The Fates of Human Societies</i>. <span class="highlight--light-purple">New York City</span>: <span class="highlight--light-green">W.W. Norton</span>; <span class="highlight--blue">1997</span>.
            </p>
        </div> <!-- // block -->

        <!-- block -->
        <div class="block" id="bibliography">
            <h3>밴쿠버 스타일 서지 목록 (Bibliography)</h3>
            <p>
              자문은 받았으나 명시적으로 인용하지 않은 저작물은 서지 목록으로 정리해야 합니다. 모든 항목은 각 항목의 첫 번째 단어의 알파벳 순서로 구성되어야 하며, 개별 항목의 형식은 참고문헌 목록을 반영해야 합니다. 참고문헌 목록과 달리, 서지 목록에는 번호를 매길 필요가 없습니다.
            </p>
        </div> <!-- // block -->
    </div>
</template>

<script>
export default {
    name: "QuickGuideVancouver"
}
</script>



// WEBPACK FOOTER //
// src/components/newQuickguide/ko/vancouver.vue